import { useTimeout, useViewportSize } from '@mantine/hooks';
import { storage } from '@newo/storage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { runtimeConfig } from '@/constants';
import { useHostContext } from '@/context/HostContext.tsx';
import useQueryParams from '@/hooks/useQueryParams.ts';
import { agentTrainingRealtime } from '@/services/AgentTrainingRealtime';
import { partnerMetadata } from '@/services/PartnerMetadata';
import useAppStore, { CreatorChatFlowCallState } from '@/store/app';
import { CanvasImageEffectName } from '@/types/canvas-image-effect';
import { useActorCreateMutation } from '@/utils/api.ts';
import {
	CREATOR_CHAT_FLOW_CHAT_SHOWN_STORAGE_KEY,
	CREATOR_CHAT_FLOW_RECEPTIONIST_CREATED_STORAGE_KEY,
	CREATOR_SHELL_HEADER_HEIGHT,
	EMILY_STAGE_HEIGHT_RATIO,
	FALLBACK_VIEWPORT_HEIGHT,
	FALLBACK_VIEWPORT_WIDTH,
	IMAGE_ASPECT_RATIO,
	INTRO_DURATION_MS,
	INTRO_HEART_COUNT,
	INTRO_IMAGE_SRC,
	READY_TRANSITION_DURATION_MS
} from '../constants';
import { canvasImageEffectService } from '../services/CanvasImageEffectService';
import { openCreatorChatFlowFinalizationModal } from '../utils/openCreatorChatFlowFinalizationModal';
import { openCreatorChatFlowInactivityModal } from '../utils/openCreatorChatFlowInactivityModal';
import { useCreatorCallSession } from './useCreatorCallSession';
import { useCreatorChatFlowHistory } from './useCreatorChatFlowHistory';

export const useCreatorChatFlowController = () => {
	const hasRevealStartedRef = useRef(false);
	const hasTransferStartedRef = useRef(false);
	const initialImageContainerRef = useRef<HTMLDivElement | null>(null);
	const transferredImageContainerRef = useRef<HTMLDivElement | null>(null);
	const { mutateAsync: getCreateActor } = useActorCreateMutation();
	const { localization } = useHostContext();
	const [isReceptionistCreated, setIsReceptionistCreated] = useState(
		() =>
			storage.get<boolean>(
				CREATOR_CHAT_FLOW_RECEPTIONIST_CREATED_STORAGE_KEY
			) === true
	);
	const [completedHearts, setCompletedHearts] = useState(
		isReceptionistCreated ? INTRO_HEART_COUNT : 0
	);
	const [hasShownChatBefore, setHasShownChatBefore] = useState(
		() =>
			storage.get<boolean>(CREATOR_CHAT_FLOW_CHAT_SHOWN_STORAGE_KEY) === true
	);
	const [isChatVisible, setIsChatVisible] = useState(hasShownChatBefore);
	const {
		hasOpenedFinalizationModal,
		pendingCallCreationFinished,
		setActor,
		setCreatorChatFlowHasOpenedFinalizationModal,
		setCustomerIdn,
		setPendingCallCreationFinished
	} = useAppStore(
		useShallow((state) => ({
			hasOpenedFinalizationModal:
				state.creatorChatFlowHasOpenedFinalizationModal,
			pendingCallCreationFinished:
				state.creatorChatFlowPendingCallCreationFinished,
			setActor: state.setActor,
			setCreatorChatFlowHasOpenedFinalizationModal:
				state.setCreatorChatFlowHasOpenedFinalizationModal,
			setCustomerIdn: state.setCustomerIdn,
			setPendingCallCreationFinished:
				state.setCreatorChatFlowPendingCallCreationFinished
		}))
	);
	const {
		params: { actorExternalId, customerIdn, description },
		handleRemoveQueryParam
	} = useQueryParams();
	const { height: viewportHeight, width: viewportWidth } = useViewportSize();
	const canRender = viewportWidth > 0 && viewportHeight > 0;
	const resolvedViewportHeight = viewportHeight || FALLBACK_VIEWPORT_HEIGHT;
	const resolvedViewportWidth = viewportWidth || FALLBACK_VIEWPORT_WIDTH;
	const stageHeight = Math.max(
		560,
		resolvedViewportHeight - CREATOR_SHELL_HEADER_HEIGHT
	);
	const introOverlayOffsetY = stageHeight * 0.28;
	const emilyHeight = stageHeight * EMILY_STAGE_HEIGHT_RATIO;
	const emilyWidth = emilyHeight * IMAGE_ASPECT_RATIO;
	const emilyBottomOffset = stageHeight * 0.03;
	const emilyOverflowTop = Math.max(
		0,
		emilyHeight - stageHeight - emilyBottomOffset
	);
	const emilyOverflowBottom = emilyBottomOffset;
	const emilyY =
		stageHeight - emilyHeight / 2 + emilyBottomOffset + emilyOverflowTop;
	const emilyFinalX =
		resolvedViewportWidth - emilyWidth / 2 + resolvedViewportWidth * 0.01;
	const revealChat = useCallback(() => {
		if (
			useAppStore.getState().creatorChatFlowCallState !==
			CreatorChatFlowCallState.Active
		) {
			return;
		}

		storage.set(CREATOR_CHAT_FLOW_CHAT_SHOWN_STORAGE_KEY, true);
		setHasShownChatBefore(true);
		setIsChatVisible(true);
	}, []);
	const { clear: clearChatRevealTimeout, start: startChatRevealTimeout } =
		useTimeout(revealChat, INTRO_DURATION_MS);
	const {
		audioLevel,
		handleStartCall,
		hasStartedCallBefore,
		isCallStarted,
		isStartingCall
	} = useCreatorCallSession({
		onCallStarted: () => {
			if (hasShownChatBefore) {
				setIsChatVisible(true);
				return;
			}

			clearChatRevealTimeout();
			startChatRevealTimeout();
		}
	});
	const isReady = isReceptionistCreated && !isCallStarted;
	const shouldShowNextSteps =
		hasOpenedFinalizationModal && !!pendingCallCreationFinished;
	const isTransferReady =
		isReceptionistCreated || completedHearts === INTRO_HEART_COUNT;
	const { isLoadingHistory, isSendingMessage, messages, sendMessage } =
		useCreatorChatFlowHistory(isReceptionistCreated && canRender);

	const isInactivityFlowEnabled =
		partnerMetadata.isVibeCreatorConfirmationFlowEnabled();

	const { clear: clearInactivityTimer, start: startInactivityTimer } =
		useTimeout(() => {
			if (description) {
				openCreatorChatFlowInactivityModal(description, localization.translate);
			}
		}, 10_000);

	useEffect(() => {
		if (!isInactivityFlowEnabled || !isReady || isCallStarted) {
			clearInactivityTimer();
			return;
		}

		startInactivityTimer();

		return () => {
			clearInactivityTimer();
		};
	}, [
		isInactivityFlowEnabled,
		isReady,
		isCallStarted,
		clearInactivityTimer,
		startInactivityTimer
	]);

	useEffect(() => {
		if (isReceptionistCreated) {
			setCompletedHearts(INTRO_HEART_COUNT);
			return;
		}

		const heartDurationMs = INTRO_DURATION_MS / INTRO_HEART_COUNT;
		let frameId = 0;
		let startedAt = 0;

		const tick = (now: number) => {
			if (!startedAt) {
				startedAt = now;
			}

			const elapsedMs = now - startedAt;
			const nextCompletedHearts = Math.min(
				INTRO_HEART_COUNT,
				Math.floor(elapsedMs / heartDurationMs)
			);

			setCompletedHearts(nextCompletedHearts);

			if (nextCompletedHearts < INTRO_HEART_COUNT) {
				frameId = window.requestAnimationFrame(tick);
			}
		};

		frameId = window.requestAnimationFrame(tick);

		return () => {
			window.cancelAnimationFrame(frameId);
		};
	}, [isReceptionistCreated]);

	useEffect(() => {
		if (!canRender) {
			return;
		}

		const initialContainer = initialImageContainerRef.current;
		const containerAfterTransfer = transferredImageContainerRef.current;

		if (initialContainer && containerAfterTransfer) {
			canvasImageEffectService.setScene({
				containerAfterTransfer,
				image: INTRO_IMAGE_SRC,
				initialContainer
			});
		}

		return () => {
			canvasImageEffectService.reset();
		};
	}, [canRender]);

	useEffect(() => {
		if (!isReceptionistCreated || !canRender) {
			return;
		}

		return agentTrainingRealtime.onCallCreationFinished((payload) => {
			if (hasOpenedFinalizationModal) {
				return;
			}

			setPendingCallCreationFinished(payload);

			if (isCallStarted) {
				return;
			}

			setCreatorChatFlowHasOpenedFinalizationModal(true);
			openCreatorChatFlowFinalizationModal(localization.translate);
		});
	}, [
		localization,
		canRender,
		hasOpenedFinalizationModal,
		isCallStarted,
		isReceptionistCreated,
		setCreatorChatFlowHasOpenedFinalizationModal,
		setPendingCallCreationFinished
	]);

	useEffect(() => {
		if (!canRender) {
			hasRevealStartedRef.current = false;
			return;
		}

		if (!isReceptionistCreated && !hasRevealStartedRef.current) {
			hasRevealStartedRef.current = true;
			void canvasImageEffectService.play(
				CanvasImageEffectName.Reveal,
				INTRO_DURATION_MS
			);
		}

		if (!canRender || hasTransferStartedRef.current || !isTransferReady) {
			return;
		}

		hasTransferStartedRef.current = true;
		if (!isReceptionistCreated) {
			storage.set(CREATOR_CHAT_FLOW_RECEPTIONIST_CREATED_STORAGE_KEY, true);
			setIsReceptionistCreated(true);
		}

		void canvasImageEffectService.play(
			CanvasImageEffectName.Transfer,
			isReceptionistCreated ? 0 : READY_TRANSITION_DURATION_MS
		);
	}, [canRender, isTransferReady, isReceptionistCreated]);

	useEffect(() => {
		if (customerIdn) {
			setCustomerIdn(customerIdn);
			handleRemoveQueryParam('customerIdn');
		}

		if (actorExternalId) {
			const chatConfig = {
				clientSecret: runtimeConfig.creatorClientSecret,
				connectorIdn: 'newo_chat'
			};

			getCreateActor({
				name: 'Creator Chat Flow Actor',
				client_secret: chatConfig.clientSecret,
				connector_idn: chatConfig.connectorIdn,
				external_id: actorExternalId
			}).then((actor) => {
				if (actor.id !== useAppStore.getState().actor?.id) {
					useAppStore
						.getState()
						.setCreatorChatFlowPendingCallCreationFinished(undefined);
					useAppStore
						.getState()
						.setCreatorChatFlowHasOpenedFinalizationModal(false);
				}
				setActor(actor);
				handleRemoveQueryParam('actorExternalId');
			});
		}
	}, [
		actorExternalId,
		customerIdn,
		getCreateActor,
		handleRemoveQueryParam,
		setActor,
		setCustomerIdn
	]);

	return {
		audioLevel,
		completedHearts,
		emilyFinalX,
		emilyHeight,
		emilyOverflowBottom,
		emilyOverflowTop,
		emilyWidth,
		emilyY,
		handleStartCall,
		hasStartedCallBefore,
		initialImageContainerRef,
		introOverlayOffsetY,
		isReceptionistCreated,
		isCallStarted,
		isLoadingHistory,
		isChatVisible,
		isReady,
		isSendingMessage,
		isStartingCall,
		messages,
		pendingCallCreationFinished,
		sendMessage,
		shouldShowNextSteps,
		stageHeight,
		transferredImageContainerRef
	};
};
